// @ts-nocheck
// Team-lead "Team Call Log" rows — every agent under this TL (team-lead-calls),
// scoped by the dashboard's date filter, one server page at a time. Polled like
// the agent's own call log.
import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { getApiCall } from '@/service/httpCall';
import { API_TEAM_LEAD_CALLS } from '@/service/url';
import { mergeSameCallRows } from '@/features/cti2/utils/dialerFormat';
import { CALL_LOG_EXPORT_MAX } from '@/features/cti/components/call-stats/useCallStatsData';

const POLL_MS = 30000;

// Merge CDR + app rows per agent only — one transferred call can legitimately
// show up once for each agent who handled it.
function mergePerAgent(records) {
  const byAgent = new Map();
  records.forEach((r) => {
    const key = String(r.agent_id);
    if (!byAgent.has(key)) byAgent.set(key, []);
    byAgent.get(key).push(r);
  });
  return [...byAgent.values()]
    .flatMap((rows) => mergeSameCallRows(rows))
    .sort((a, b) => new Date(b.time_of_call || 0).getTime() - new Date(a.time_of_call || 0).getTime())
    .map((row, idx) => ({ ...row, key: `${row.agent_id}:${row.id ?? idx}` }));
}

export function useTeamCallLog(startDate, endDate, enabled) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const start = moment(startDate).format('YYYY-MM-DD');
  const end = moment(endDate).format('YYYY-MM-DD');
  const filterKey = `${start}|${end}|${search}`;
  const [pageState, setPageState] = useState({ key: filterKey, pageIndex: 0, pageSize: 10 });
  // A new date range or search starts again at page 1.
  const paging = pageState.key === filterKey ? pageState : { ...pageState, key: filterKey, pageIndex: 0 };
  const reqRef = useRef({ seq: 0 });
  reqRef.current = { ...reqRef.current, start, end, search, paging, filterKey };

  const refresh = useCallback(async (silent = false) => {
    const { start: s, end: e, search: q, paging: p } = reqRef.current;
    if (s > e) return; // mid range-pick: "from" moved past the old "to"
    const seq = ++reqRef.current.seq;
    if (!silent) setLoading(true);
    try {
      const resp = await getApiCall(API_TEAM_LEAD_CALLS, {
        start_date: s,
        end_date: e,
        page: p.pageIndex + 1,
        limit: p.pageSize,
        search: q,
      });
      if (seq !== reqRef.current.seq) return; // a newer page/filter request owns the table
      // ponytail: merged per page, so a pair split across a page edge shows once on each page.
      setRows(Array.isArray(resp?.callRecords) ? mergePerAgent(resp.callRecords) : []);
      setTotal(Number(resp?.total) || 0);
    } catch {
      if (!silent && seq === reqRef.current.seq) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (seq === reqRef.current.seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(() => {
      if (!document.hidden) refresh(true);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, filterKey, paging.pageIndex, paging.pageSize, refresh]);

  const changePaging = useCallback(
    (next) => setPageState({ key: reqRef.current.filterKey, pageIndex: next.pageIndex, pageSize: next.pageSize }),
    []
  );

  // Whole filtered log (date range + search) for "Export these calls", capped server-side.
  const fetchExport = useCallback(async () => {
    const { start: s, end: e, search: q } = reqRef.current;
    const resp = await getApiCall(API_TEAM_LEAD_CALLS, {
      start_date: s,
      end_date: e,
      page: 1,
      limit: CALL_LOG_EXPORT_MAX,
      export: 1,
      search: q,
    });
    const raw = Array.isArray(resp?.callRecords) ? resp.callRecords : [];
    return { rows: mergePerAgent(raw), total: Number(resp?.total) || 0, fetched: raw.length };
  }, []);

  return {
    rows,
    loading,
    refresh,
    paging: { pageIndex: paging.pageIndex, pageSize: paging.pageSize, rowCount: total, onChange: changePaging },
    changeSearch: setSearch,
    fetchExport,
  };
}
